import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, MapPin, Bell, AlertCircle } from 'lucide-react';
import { Appointment } from '../dashboard/widgets/types/appointment';
import './appointment-reminder.css';

interface AppointmentReminderProps {
  appointment: Appointment;
  onClose: () => void;
  onSnooze: (minutes: number) => void;
}

const AppointmentReminder: React.FC<AppointmentReminderProps> = ({
  appointment,
  onClose,
  onSnooze
}) => {
  const [snoozeMinutes, setSnoozeMinutes] = useState(5);
  const [isSnoozing, setIsSnoozing] = useState(false);

  const handleSnooze = () => {
    setIsSnoozing(true);
    onSnooze(snoozeMinutes);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const formatTime = (time: string) => {
    return time;
  };

  const getTimeUntil = () => {
    const now = new Date();
    const appointmentDate = new Date(`${appointment.date}T${appointment.startTime}`);
    const diffMs = appointmentDate.getTime() - now.getTime();
    const diffMins = Math.round(diffMs / 60000);

    if (diffMins <= 0) {
      return 'jetzt';
    } else if (diffMins < 60) {
      return `in ${diffMins} Minute${diffMins !== 1 ? 'n' : ''}`;
    } else {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `in ${hours} Stunde${hours !== 1 ? 'n' : ''}${mins > 0 ? ` ${mins} Min` : ''}`;
    }
  };

  const snoozeOptions = [
    { value: 5, label: '5 Min' },
    { value: 10, label: '10 Min' },
    { value: 15, label: '15 Min' },
    { value: 30, label: '30 Min' }
  ];

  const content = (
    <AnimatePresence>
      <div className="reminder-container">
        <motion.div
          initial={{ opacity: 0, x: 100, y: -20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: 100, scale: 0.9 }}
          className="appointment-reminder-card"
        >
          {/* Header */}
          <div className="reminder-header">
            <div className="reminder-header-left">
              <div className="reminder-icon">
                <Bell className="w-5 h-5" />
              </div>
              <div className="reminder-badge">
                <Clock className="w-3 h-3" />
                <span>{getTimeUntil()}</span>
              </div>
            </div>
            <button onClick={onClose} className="reminder-close-btn">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="reminder-content">
            <h3 className="reminder-title">{appointment.title}</h3>

            <div className="reminder-details">
              <div className="reminder-detail-row">
                <Clock className="w-4 h-4" />
                <span>
                  {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                </span>
              </div>

              {appointment.location && (
                <div className="reminder-detail-row">
                  <MapPin className="w-4 h-4" />
                  <span>{appointment.location}</span>
                </div>
              )}

              {appointment.description && (
                <div className="reminder-description">
                  <AlertCircle className="w-4 h-4" />
                  <p>{appointment.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Snooze Controls */}
          <div className="reminder-snooze-section">
            <label className="snooze-label">Nochmal erinnern:</label>

            <div className="snooze-slider-container">
              <input
                type="range"
                min="5"
                max="30"
                step="5"
                value={snoozeMinutes}
                onChange={(e) => setSnoozeMinutes(parseInt(e.target.value))}
                className="snooze-slider"
              />
              <div className="snooze-value-display">
                {snoozeMinutes} Min
              </div>
            </div>

            <div className="snooze-quick-options">
              {snoozeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSnoozeMinutes(option.value)}
                  className={`snooze-option-btn ${snoozeMinutes === option.value ? 'active' : ''}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="reminder-actions">
            <button
              onClick={onClose}
              className="reminder-btn reminder-btn-dismiss"
            >
              Schließen
            </button>
            <button
              onClick={handleSnooze}
              className="reminder-btn reminder-btn-snooze"
              disabled={isSnoozing}
            >
              <Bell className="w-4 h-4" />
              {isSnoozing ? 'Wird gesetzt...' : `In ${snoozeMinutes} Min erinnern`}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default AppointmentReminder;
